import React from 'react'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Bắt lỗi render/lifecycle chưa được xử lý ở bất kỳ đâu trong cây component con,
 * hiện thông báo + chi tiết lỗi thay vì để React âm thầm xóa trắng toàn bộ trang
 * (hành vi mặc định khi không có Error Boundary nào bắt lỗi).
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Vẫn log đầy đủ ra console để không mất thông tin debug hiện có.
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack)
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
        <div className="max-w-lg rounded-lg border border-rose-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-bold text-rose-700">Đã xảy ra lỗi khi hiển thị trang</h1>
          <p className="mt-2 text-sm text-slate-600">
            Rất tiếc, trang này gặp lỗi và không hiển thị được. Vui lòng chụp lại nội dung bên dưới
            và báo cho bộ phận kỹ thuật.
          </p>
          <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-slate-100 p-3 text-left text-xs text-slate-700">
            {error.name}: {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-4 rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800"
          >
            Tải lại trang
          </button>
        </div>
      </div>
    )
  }
}
